// 複数Y軸チャートで、各系列をどの軸に載せるかを yaxis[].seriesName として組み立てる(#1359)。
//
// ApexCharts(6.x)の軸割り当ては yaxis[].seriesName と series[].name の文字列一致だけで決まり、
// series[].yAxisIndex は読まない(型定義上は annotation 専用)。名前で紐付かない系列は
// 「未参照の軸へ先頭から1本ずつ → 余りは最後の軸」に寄せられるため(CoreUtils#setSeriesYAxisMappings)、
// yAxisIndex で意図を書いても系列数が軸数より多いと意図しない軸に載っていた。
//
// 規則:
// - yaxis が2本以上、かつ yAxisIndex(整数)を持つ系列が1本以上あるときだけ組み立て直す。
//   それ以外は受け取った yaxis をそのまま返す(従来どおり)。
// - 既存の seriesName が実在する系列名を指していれば、その系列はその軸に載せる(明示指定を優先)。
//   同じ系列を複数の軸が指している場合は先の軸を採る。実在しない名前('左軸' など)は無視する。
// - 明示指定されていない系列は yAxisIndex の軸に載せる。未指定・範囲外の yAxisIndex は軸0扱い。
// - 各軸の seriesName は系列名の配列にする(配列にすると ApexCharts は名前どおりに割り当てる)。
// - 次の場合は名前で表せない・ApexCharts が未定義の系列を割り当ててしまうため、従来どおりにする:
//   名前が無い/重複する系列がある、系列が1本も載らない軸ができる(後者は設定ミスの可能性が高いので console.warn で空の軸を示す)。
export function assignSeriesToYAxes(yaxis, series) {
  if (!Array.isArray(yaxis) || yaxis.length < 2) return yaxis
  if (!Array.isArray(series) || series.length === 0) return yaxis
  if (!series.some((s) => Number.isInteger(s?.yAxisIndex))) return yaxis

  const names = series.map((s) => s?.name)
  if (names.some((name) => typeof name !== "string" || name === "")) return yaxis
  const nameSet = new Set(names)
  if (nameSet.size !== names.length) return yaxis

  const axisByName = new Map()
  yaxis.forEach((axis, axisIndex) => {
    toNameList(axis?.seriesName).forEach((name) => {
      if (nameSet.has(name) && !axisByName.has(name)) axisByName.set(name, axisIndex)
    })
  })

  series.forEach((s) => {
    if (axisByName.has(s.name)) return
    const index = s.yAxisIndex
    const inRange = Number.isInteger(index) && index >= 0 && index < yaxis.length
    axisByName.set(s.name, inRange ? index : 0)
  })

  const namesByAxis = yaxis.map(() => [])
  names.forEach((name) => namesByAxis[axisByName.get(name)].push(name))
  const emptyAxes = namesByAxis.flatMap((axisNames, axisIndex) => (axisNames.length === 0 ? [axisIndex] : []))
  if (emptyAxes.length > 0) {
    console.warn(
      `apex-chart: 系列が1本も載らない Y 軸(yaxis[${emptyAxes.join(", ")}])があるため、yAxisIndex からの軸割り当てをせず元の設定のまま描画します。` +
      "series の yAxisIndex か yaxis の本数を見直してください。",
      { yAxisIndexes: series.map((s) => ({ name: s.name, yAxisIndex: s.yAxisIndex })) }
    )
    return yaxis
  }

  return yaxis.map((axis, axisIndex) => ({ ...axis, seriesName: namesByAxis[axisIndex] }))
}

function toNameList(seriesName) {
  if (Array.isArray(seriesName)) return seriesName
  if (typeof seriesName === "string" && seriesName !== "") return [seriesName]
  return []
}
